import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  Mail,
  Phone,
  MapPin,
  ArrowUp,
  Video,
  Share2,
  Layers,
  ShoppingBag,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  X,
} from "lucide-react";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import { Link } from "react-router-dom";

export default function Footer() {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePrivacyPolicyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrivacyPolicy(true);
  };

  const handleTermsOfServiceClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTermsOfService(true);
  };

  const quickLinks = [
    // { name: "Create Reels", href: "#features" },
    // { name: "Share Pixs", href: "#features" },
    // { name: "Collections", href: "#features" },
    { name: "Features", href: "#features" },
    { name: "How It Works", href: "#how-it-works" },
    { name: "About Us", href: "#about" },
  ];

  const supportLinks = [
    // { name: "Help Center", href: "#contact" },
    { name: "Contact Support", href: "#contact" },
    { name: "Privacy Policy", href: "#contact", isPrivacyPolicy: true },
    { name: "Terms of Service", href: "#contact", isTermsOfService: true },
    { name: "FAQs", href: "/faqs", isFaq: true },
    // { name: "Community Guidelines", href: "#contact" },
    // { name: "Safety Center", href: "#contact" },
  ];

  const socialLinks = [
    { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
    { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
    { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
    { icon: Linkedin, href: "https://linkedin.com", label: "LinkedIn" },
    { icon: Youtube, href: "https://youtube.com", label: "YouTube" },
  ];

  const features = [
    { icon: "/images/reels_icon.png", text: "Short Reels", color: "bg-grad-orange-1" },
    { icon: "/images/pix_icon.png", text: "Pix Sharing", color: "bg-brand-orange" },
    { icon: "/images/pouch_icon.png", text: "Pix Pouches", color: "bg-grad-orange-2" },
    { icon: "/images/chat_icon.png", text: "Chat & Call", color: "bg-grad-orange-1" },
    { icon: "/images/shop_icon.png", text: "Shopping", color: "bg-orange-old" },
    {icon: "/images/memorycapsule.jpeg", text: "Memorycapsule", color: "bg-orange-old" }
  ];

  return (
    <footer className="bg-gradient-to-br from-background via-secondary/10 to-primary/5 border-t border-border">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Brand Section */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <img
                src="/images/logo_new_nobg.png"
                alt="VREELS Logo"
                className="h-10 w-auto"
                style={{
                  filter:
                    "drop-shadow(0 0 8px rgba(254, 62, 6, 0.3)) brightness(1.2) contrast(1.15) saturate(1.05)",
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(254, 62, 6, 0.03))",
                  borderRadius: "8px",
                  padding: "6px",
                  border: "1px solid rgba(254, 62, 6, 0.15)",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div
                className="h-10 w-10 bg-gradient-to-br from-brand-orange to-grad-orange-1 rounded-lg items-center justify-center text-white text-sm font-bold"
                style={{ display: "none" }}
              >
                VR
              </div>
              <span className="text-2xl font-bold gradient-text-brand">
                VREELS
              </span>
            </div>

            <p className="text-muted-foreground leading-relaxed">
              Create short reels, share pixs, build collection of pixs, and shop
              products all in one platform. Join the creative community that's
              changing how we connect and discover.
            </p>

            {/* Core Features */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground">
                Our Core Features:
              </p>
              <div className="grid grid-cols-2 gap-2">
                {features.map((feature, index) => {
                  return (
                    <div
                      key={index}
                      className="flex items-center space-x-2 text-sm text-muted-foreground"
                    >
                      <div className={`h-6 w-6 flex items-center justify-center rounded-full ${feature.color}`}>
                        <img src={feature.icon} alt={feature.text} className="h-4 w-4" />
                      </div>
                      <span>{feature.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-foreground">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-muted-foreground hover:text-primary transition-colors duration-200 hover:underline cursor-pointer"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support & Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-foreground">
              Support & Resources
            </h3>

            <ul className="space-y-3">
              {supportLinks.map((link, index) => (
                <li key={index}>
                  {link.isFaq ? (
                    <Link
                      to="/faqs"
                      className="text-muted-foreground hover:text-[#FE3E06] transition-colors duration-200 hover:underline cursor-pointer"
                    >
                      {link.name}
                    </Link>
                  ) : link.isPrivacyPolicy ? (
                    <Link
                      to="/privacy-policy"
                      className="text-muted-foreground hover:text-[#FE3E06] transition-colors duration-200 hover:underline cursor-pointer"
                    >
                      {link.name}
                    </Link>
                  ) : link.isTermsOfService ? (
                    <Link
                      to="/terms-of-service"
                      className="text-muted-foreground hover:text-[#FE3E06] transition-colors duration-200 hover:underline cursor-pointer"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        document
                          .querySelector(link.href)
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="text-muted-foreground hover:text-[#FE3E06] transition-colors duration-200 hover:underline cursor-pointer"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-foreground">
              Get In Touch
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Email</p>
                  <a
                    href="mailto:support@vreels.com"
                    className="text-sm text-muted-foreground hover:text-primary"
                  >
                    support@vreels.com
                  </a>
                </div>
              </div>

              {/* <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Phone</p>
                  <p className="text-sm text-muted-foreground">+1</p>
                </div>
              </div> */}

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground mb-2">
                      Offices
                    </p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs font-semibold text-foreground mb-1">USA</p>
                        <p className="text-sm text-muted-foreground">Saginaw, Texas</p>
                        <p className="text-sm text-muted-foreground">Atlanta, Georgia</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground mb-1">India</p>
                        <p className="text-sm text-muted-foreground">Hyderabad, Telangana</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            {/* <div className="mt-6">
              <p className="text-sm font-medium text-foreground mb-3">
                Follow Us
              </p>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-secondary hover:bg-primary hover:text-white rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                      aria-label={social.label}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            </div> */}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-muted-foreground">
            <p>&copy; 2025 Rudrakali Networks Pvt Ltd. All rights reserved.</p>
            <div className="flex items-center gap-4">
              {/* <span>Made with passion for creators</span> */}
              <span className="hidden md:inline">|</span>
              <span>v1.0.0</span>
            </div>
          </div>

          {/* Back to Top Button */}
          <button
            onClick={scrollToTop}
            className="group flex items-center space-x-2 px-4 py-2 bg-secondary hover:bg-primary hover:text-white rounded-lg transition-all duration-200 hover:scale-105"
            aria-label="Back to top"
          >
            <span className="text-sm font-medium">Back to Top</span>
            <ArrowUp className="h-4 w-4 group-hover:transform group-hover:-translate-y-1 transition-transform duration-200" />
          </button>
        </div>
      </div>

      {/* Modals */}
      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
      />
      <TermsOfServiceModal
        isOpen={showTermsOfService}
        onClose={() => setShowTermsOfService(false)}
      />
    </footer>
  );
}

// Modal components rendered via Portal
export function PrivacyPolicyModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        zIndex: 999999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "896px",
          maxHeight: "90vh",
          backgroundColor: "hsl(215, 16%, 8%)",
          borderRadius: "16px",
          border: "1px solid hsl(215, 16%, 20%)",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            zIndex: 10,
            padding: "8px",
            backgroundColor: "hsl(210, 11%, 15%)",
            border: "none",
            borderRadius: "8px",
            color: "white",
            cursor: "pointer",
          }}
          aria-label="Close Privacy Policy"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Privacy Policy Content */}
        <div style={{ overflow: "auto", maxHeight: "90vh" }}>
          <PrivacyPolicy isModal={true} />
        </div>
      </div>
    </div>,
    document.body
  );
}

export function TermsOfServiceModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        zIndex: 999999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "896px",
          maxHeight: "90vh",
          backgroundColor: "hsl(215, 16%, 8%)",
          borderRadius: "16px",
          border: "1px solid hsl(215, 16%, 20%)",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            zIndex: 10,
            padding: "8px",
            backgroundColor: "hsl(210, 11%, 15%)",
            border: "none",
            borderRadius: "8px",
            color: "white",
            cursor: "pointer",
          }}
          aria-label="Close Terms of Service"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Terms of Service Content */}
        <div style={{ overflow: "auto", maxHeight: "90vh" }}>
          <TermsOfService isModal={true} />
        </div>
      </div>
    </div>,
    document.body
  );
}
